import { ApiResult, HttpClient } from "../http.js";
import {
  AssetCategory,
  AssetCategoryDto,
  AssetCondition,
  AssetDto,
  AssetMaintenanceLogDto,
  AssetMaintenanceLogType,
  AssetMaintenanceScheduleDto,
  AssetMaintenanceScheduleStatus,
  AssetStatus,
} from "../types/assets.js";
import { AssetProjectDto } from "../types/projects.js";

export interface AssetDocumentPayload {
  name: string;
  url: string;
  type: string;
  uploadedAt: string;
}

export interface CreateAssetPayload {
  customerId?: string | null;
  customerName: string;
  locationId?: string | null;
  locationAddress?: string | null;
  locationDescription?: string | null;
  name: string;
  description?: string | null;
  category: AssetCategory;
  assetCategoryId?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  assetTag?: string | null;
  installationDate?: string | null;
  installedBy?: string | null;
  warrantyExpiry?: string | null;
  specifications?: Record<string, string> | null;
  capacity?: string | null;
  voltage?: string | null;
  status: AssetStatus;
  lastInspectionDate?: string | null;
  nextInspectionDue?: string | null;
  condition?: AssetCondition | null;
  serviceAgreementIds?: string[] | null;
  photos?: string[] | null;
  documents?: AssetDocumentPayload[] | null;
  notes?: string | null;
  certificationRequired?: boolean;
  certificationExpiry?: string | null;
  certificationType?: string | null;
  complianceNotes?: string | null;
  id?: string;
}

export interface UpdateAssetPayload {
  customerId?: string | null;
  customerName: string;
  locationId?: string | null;
  locationAddress?: string | null;
  locationDescription?: string | null;
  name: string;
  description?: string | null;
  category: AssetCategory;
  assetCategoryId?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  assetTag?: string | null;
  installationDate?: string | null;
  installedBy?: string | null;
  warrantyExpiry?: string | null;
  specifications?: Record<string, string> | null;
  capacity?: string | null;
  voltage?: string | null;
  status: AssetStatus;
  lastInspectionDate?: string | null;
  nextInspectionDue?: string | null;
  condition?: AssetCondition | null;
  serviceAgreementIds?: string[] | null;
  photos?: string[] | null;
  documents?: AssetDocumentPayload[] | null;
  notes?: string | null;
  certificationRequired?: boolean;
  certificationExpiry?: string | null;
  certificationType?: string | null;
  complianceNotes?: string | null;
}

export interface ListAssetsQuery {
  status?: AssetStatus;
  category?: AssetCategory;
  customerId?: string;
  needsInspection?: boolean;
  /** The tenant category (`AssetCategoryDto.id`) — NOT the legacy `category` enum above. */
  assetCategoryId?: string;
  /** Matched server-side against name, customer name, serial number, asset tag OR location address. */
  search?: string;
  /** 1-based. Omit for the full (unpaged) list — identical to the pre-paging response. */
  page?: number;
  pageSize?: number;
  /** `YYYY-MM-DD`: only assets with an active maintenance schedule due on or before this date. */
  maintenanceDueOnOrBefore?: string;
}

export interface AssetMaintenancePartPayload {
  name: string;
  quantity: number;
  cost?: number | null;
}

export interface AddMaintenanceLogPayload {
  date: string;
  type: AssetMaintenanceLogType;
  description: string;
  findings?: string | null;
  actionsPerformed?: string | null;
  statusBefore?: AssetStatus | null;
  statusAfter?: AssetStatus | null;
  conditionBefore?: AssetCondition | null;
  conditionAfter?: AssetCondition | null;
  partsUsed?: AssetMaintenancePartPayload[] | null;
  hoursSpent?: number | null;
  serviceAgreementId?: string | null;
  caseId?: string | null;
  serviceLogId?: string | null;
  performedBy: string;
  performedByName?: string | null;
  photos?: string[] | null;
  signature?: string | null;
  followUpRequired?: boolean | null;
  followUpNotes?: string | null;
  nextMaintenanceDate?: string | null;
  id?: string;
}

export interface CreateAssetMaintenanceSchedulePayload {
  intervalDays: number;
  estimatedDurationMinutes: number;
  startDate: string;
  status: AssetMaintenanceScheduleStatus;
  lastCompletedDate?: string | null;
  projectTemplateId?: string | null;
  expiresAt?: string | null;
  /** Omitted = on for normal callers (off for the migration actor). */
  generateProjectWhenDue?: boolean;
  /** @deprecated Ignored by the generation sweep; the company lead time is used. */
  projectGenerationLeadDays?: number;
  id?: string;
  title?: string | null;
  priceExclVat?: number | null;
  currency?: string | null;
  billToCustomerId?: string | null;
}

export interface UpdateAssetMaintenanceSchedulePayload {
  intervalDays: number;
  estimatedDurationMinutes: number;
  startDate: string;
  projectTemplateId?: string | null;
  expiresAt?: string | null;
  /** Omitted = unchanged on the server. */
  generateProjectWhenDue?: boolean;
  /** @deprecated Ignored by the generation sweep; the company lead time is used. */
  projectGenerationLeadDays?: number;
  /** Omitted fields below are left unchanged by the server. */
  title?: string | null;
  priceExclVat?: number | null;
  currency?: string | null;
  billToCustomerId?: string | null;
  clearPrice?: boolean;
  clearBillTo?: boolean;
}

export interface CompleteAssetMaintenanceSchedulePayload {
  completedDate: string;
  description: string;
  findings?: string | null;
  actionsPerformed?: string | null;
  statusAfter?: AssetStatus | null;
  conditionAfter?: AssetCondition | null;
  hoursSpent?: number | null;
  performedBy: string;
  performedByName?: string | null;
  logId?: string;
  /** The serviceordre this visit belongs to. Omitted: the order generated for the agreement's current due date. */
  projectId?: string | null;
}

export interface CreateAssetCategoryPayload {
  name: string;
  key: string;
}

export interface UpdateAssetCategoryPayload {
  name: string;
}

export class AssetsApi {
  constructor(private readonly http: HttpClient) {}

  list(query: ListAssetsQuery = {}): Promise<ApiResult<AssetDto[]>> {
    return this.http.get<AssetDto[]>("/api/assets", { query: { ...query } });
  }

  get(id: string): Promise<ApiResult<AssetDto>> {
    return this.http.get<AssetDto>(`/api/assets/${encodeURIComponent(id)}`);
  }

  create(payload: CreateAssetPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/assets", { body: payload });
  }

  update(id: string, payload: UpdateAssetPayload): Promise<ApiResult<AssetDto>> {
    return this.http.put<AssetDto>(`/api/assets/${encodeURIComponent(id)}`, { body: payload });
  }

  remove(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/assets/${encodeURIComponent(id)}`);
  }

  addMaintenanceLog(assetId: string, payload: AddMaintenanceLogPayload): Promise<ApiResult<string>> {
    return this.http.post<string>(
      `/api/assets/${encodeURIComponent(assetId)}/maintenance-logs`,
      { body: payload }
    );
  }

  listMaintenanceLogs(assetId: string): Promise<ApiResult<AssetMaintenanceLogDto[]>> {
    return this.http.get<AssetMaintenanceLogDto[]>(
      `/api/assets/${encodeURIComponent(assetId)}/maintenance-logs`
    );
  }

  listMaintenanceEvidence(logId: string) {
    return this.http.get<import('../types/documents.js').DocumentDto[]>(
      `/api/assets/maintenance-logs/${encodeURIComponent(logId)}/evidence`,
    );
  }

  listCategories(includeArchived = false): Promise<ApiResult<AssetCategoryDto[]>> {
    return includeArchived
      ? this.http.get<AssetCategoryDto[]>('/api/assets/categories', {
        query: { includeArchived: true },
      })
      : this.http.get<AssetCategoryDto[]>('/api/assets/categories');
  }

  createCategory(payload: CreateAssetCategoryPayload): Promise<ApiResult<AssetCategoryDto>> {
    return this.http.post<AssetCategoryDto>('/api/assets/categories', { body: payload });
  }

  updateCategory(id: string, payload: UpdateAssetCategoryPayload): Promise<ApiResult<AssetCategoryDto>> {
    return this.http.put<AssetCategoryDto>(`/api/assets/categories/${encodeURIComponent(id)}`, { body: payload });
  }

  archiveCategory(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/assets/categories/${encodeURIComponent(id)}`);
  }

  /** The projects (serviceordrer) this anlæg is linked to, newest first. */
  listProjects(assetId: string): Promise<ApiResult<AssetProjectDto[]>> {
    return this.http.get<AssetProjectDto[]>(`/api/assets/${encodeURIComponent(assetId)}/projects`);
  }

  listMaintenanceSchedules(assetId: string): Promise<ApiResult<AssetMaintenanceScheduleDto[]>> {
    return this.http.get<AssetMaintenanceScheduleDto[]>(
      `/api/assets/${encodeURIComponent(assetId)}/maintenance-schedules`
    );
  }

  listDueMaintenanceSchedules(dueOnOrBefore?: string): Promise<ApiResult<AssetMaintenanceScheduleDto[]>> {
    return dueOnOrBefore
      ? this.http.get<AssetMaintenanceScheduleDto[]>("/api/assets/maintenance-schedules/due", {
        query: { dueOnOrBefore },
      })
      : this.http.get<AssetMaintenanceScheduleDto[]>("/api/assets/maintenance-schedules/due");
  }

  createMaintenanceSchedule(
    assetId: string,
    payload: CreateAssetMaintenanceSchedulePayload,
  ): Promise<ApiResult<AssetMaintenanceScheduleDto>> {
    return this.http.post<AssetMaintenanceScheduleDto>(
      `/api/assets/${encodeURIComponent(assetId)}/maintenance-schedules`,
      { body: payload },
    );
  }

  updateMaintenanceSchedule(
    id: string,
    payload: UpdateAssetMaintenanceSchedulePayload,
  ): Promise<ApiResult<AssetMaintenanceScheduleDto>> {
    return this.http.put<AssetMaintenanceScheduleDto>(
      `/api/assets/maintenance-schedules/${encodeURIComponent(id)}`,
      { body: payload },
    );
  }

  pauseMaintenanceSchedule(id: string, reason?: string | null): Promise<ApiResult<AssetMaintenanceScheduleDto>> {
    return this.http.post<AssetMaintenanceScheduleDto>(
      `/api/assets/maintenance-schedules/${encodeURIComponent(id)}/pause`,
      { body: { reason: reason ?? null } },
    );
  }

  resumeMaintenanceSchedule(id: string): Promise<ApiResult<AssetMaintenanceScheduleDto>> {
    return this.http.post<AssetMaintenanceScheduleDto>(
      `/api/assets/maintenance-schedules/${encodeURIComponent(id)}/resume`,
    );
  }

  /** Ends the agreement — nothing further is owed and it leaves the due queue. */
  expireMaintenanceSchedule(id: string, endedOn?: string | null): Promise<ApiResult<AssetMaintenanceScheduleDto>> {
    return this.http.post<AssetMaintenanceScheduleDto>(
      `/api/assets/maintenance-schedules/${encodeURIComponent(id)}/expire`,
      { body: { endedOn: endedOn ?? null } },
    );
  }

  completeMaintenanceSchedule(
    id: string,
    payload: CompleteAssetMaintenanceSchedulePayload,
  ): Promise<ApiResult<string>> {
    return this.http.post<string>(
      `/api/assets/maintenance-schedules/${encodeURIComponent(id)}/complete`,
      { body: payload },
    );
  }
}
